import React, { useRef } from "react"
import styled from "styled-components"
import { motion } from "framer-motion"

import { useOnScreen } from "../../hooks"
import ContentWrapper from "../../styles/contentWrapper"
import Underlining from "../../styles/underlining"

const StyledSection = styled.section`
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.colors.background};
  margin-top: 8rem;
`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;

    /* MOBILE: Use standard small padding */
    padding-left: 1.5rem;
    padding-right: 1.5rem;

    display: flex;
    flex-direction: column;

    /* DESKTOP: Remove wrapper padding (if your design relies on inner margins) */
    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding-left: 0rem;
      padding-right: 0rem;
    }

    .section-title {
      margin-bottom: 2rem;

      /* MOBILE: Align left */
      margin-left: 2rem;

      /* DESKTOP: Restore your custom indentation */
      @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
        margin-left: 3.1rem;
      }
    }
  }
`

const FocusItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 3rem;

  /* Card styling */
  background: ${({ theme }) => theme.colors.card};
  border: 0.05px solid ${({ theme }) => theme.colors.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: 0.05rem;
  box-shadow: 0 0 47px rgba(225, 33, 193, 0.3);
  padding: 1.5rem;
  cursor: pointer;
  transform-style: preserve-3d;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px ${({ theme }) => theme.colors.boxShadowHover};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: row;
    align-items: left;
    padding: 2rem;
  }

  .content {
    width: 100%;
    max-width: 100rem;
    display: flex;
    flex-direction: column;

    /* MOBILE: Reset margins to zero or small values */
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    margin-top: 0.5rem;

    /* DESKTOP: Restore your specific spacing */
    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      margin-left: 1rem;
      margin-right: 1rem;
    }
  }
  .category {
    font-size: 0.875rem;
    line-height: 1rem;
    text-transform: uppercase;
    letter-spacing: +1px;
    color: ${({ theme }) => theme.colors.subtext};
  }
  .title {
    margin-top: 0.625rem;
    margin-bottom: 0.625rem;
    font-size: 1.375rem;
    line-height: 1.625rem;
    font-weight: 700;
  }
  .description {
    margin-top: 1rem;
    line-height: 1.6;
  }
  .tags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 1.5rem;
    line-height: 1.2rem;
    span {
      margin-right: 1rem;
      margin-bottom: 1rem;
    }
  }
  .progress-bar {
    width: 100%;
    height: 8px;
    background: ${({ theme }) => theme.colors.tertiary};
    border-radius: 4px;
    margin-top: 1.5rem;
    overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: #39ff14;
    border-radius: 4px;
    transition: width 0.3s ease-out;
  }
`

const CurrentFocus = () => {
  const tRef = useRef()
  const tOnScreen = useOnScreen(tRef)
  const tVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const fVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  // Dummy data
  const focusItems = [
    {
      category: "School Work",
      title: "Parallel and High Throughput Computing (CS 557)",
      description:
        "Currently learning how to represent the Poisson equation using a 3D Laplacian matrix and how to use the OpenMP API to parallelize the computation. I currently get a memory bandwidth less than 60% of the peak bandwidth which is incorrect according to project spec.",
      tags: ["C++", "OpenMP"],
      progress: 75,
    },
    {
      category: "Reading",
      title: "Software Security (CS542)",
      description:
        "I learnt a lot of new things about security and how there is always scope for vulnerabilities in software. Right now we're just at the beginning and learning about how truncation, overflows, underflows, incorrect calculations, or non-standardized processor architectures and language specifications can introduce a ton of vulnerabilites/potential threats in software.",
      tags: ["Java", "Rust", "C", "C++", "C#", "Ada"],
      progress: 60,
    },
  ]

  return (
    <StyledSection id="changelog">
      <StyledContentWrapper>
        <motion.div
          ref={tRef}
          variants={tVariants}
          animate={tOnScreen ? "visible" : "hidden"}
        >
          <h3 className="section-title">Journaling</h3>
        </motion.div>
        {focusItems.map((item, index) => (
          <FocusItem
            key={index}
            variants={fVariants}
            initial="hidden"
            animate={tOnScreen ? "visible" : "hidden"}
            transition={{ delay: index * 0.2 }}
            whileHover={{
              rotateX: -5,
              rotateY: 5,
              scale: 1.02,
              transition: { duration: 0.2 },
            }}
            style={{ perspective: 1000 }}
          >
            <div className="content">
              <div className="category">{item.category}</div>
              <div className="title">{item.title}</div>
              <div className="description">{item.description}</div>
              <div className="tags">
                {item.tags.map(tag => (
                  <Underlining key={tag} highlight>
                    {tag}
                  </Underlining>
                ))}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            </div>
          </FocusItem>
        ))}
      </StyledContentWrapper>
    </StyledSection>
  )
}

export default CurrentFocus
